import axios from 'axios';
import { sessionResponseSchema, SessionResponse } from '../schemas/session';
import { challengeResponseSchema, ChallengeResponse } from '../schemas/challenge';
import { authErrorResponseSchema, AuthErrorCode, getErrorMessage } from '../schemas/errors';

export type LoginResponse = SessionResponse | ChallengeResponse;

export interface AuthClientConfig {
  passwordLoginEndpoint: string;
  challengeStartEndpoint?: string;
  challengeVerifyEndpoint?: string;
  passwordResetStartEndpoint?: string;
  passwordResetCompleteEndpoint?: string;
  emailLoginStartEndpoint?: string;
  emailLoginVerifyEndpoint?: string;
  backupTokensGenerateEndpoint?: string;
  backupTokensCountEndpoint?: string;
}

export class AuthClientError extends Error {
  code: AuthErrorCode;

  constructor(code: AuthErrorCode, message?: string) {
    super(message || getErrorMessage(code));
    this.code = code;
    this.name = 'AuthClientError';
  }
}

function parseErrorResponse(data: unknown): AuthClientError {
  const parsed = authErrorResponseSchema.safeParse(data);
  if (parsed.success) {
    return new AuthClientError(parsed.data.data.code, parsed.data.data.message);
  }
  return new AuthClientError('unauthorized');
}

function deriveEndpoint(baseEndpoint: string, relativePath: string): string {
  // Given "/admin/api/v2/login/password", derive other endpoints
  // by replacing the last path segment(s)
  const basePath = baseEndpoint.replace(/\/login\/password$/, '');
  return `${basePath}${relativePath}`;
}

export function createAuthClient(config: AuthClientConfig) {
  const headers = { 'Content-Type': 'application/json' };

  async function passwordLogin(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post(
        config.passwordLoginEndpoint,
        { email, password },
        {
          headers,
          validateStatus: (status) => status === 200 || status === 201 || status === 202,
        }
      );

      if (response.status === 200 || response.status === 201) {
        return sessionResponseSchema.parse(response.data);
      }
      if (response.status === 202) {
        return challengeResponseSchema.parse(response.data);
      }
      throw new Error('Unexpected response status');
    } catch (error) {
      console.error('Login error:', error);
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function startChallenge(
    challengeToken: string,
    challengeType: string
  ): Promise<ChallengeResponse> {
    const endpoint =
      config.challengeStartEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/login/challenge/start');

    const payload = { data: { type: challengeType } };

    try {
      const response = await axios.post(endpoint, payload, {
        headers: {
          ...headers,
          Authorization: `Bearer ${challengeToken}`,
        },
      });
      return challengeResponseSchema.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function verifyChallenge(
    challengeToken: string,
    challengeType: string,
    code: string,
    options?: { requestZToken?: boolean }
  ): Promise<SessionResponse> {
    const endpoint =
      config.challengeVerifyEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/login/challenge/verify');

    const data: Record<string, unknown> = { type: challengeType, answer: code };
    if (options?.requestZToken && challengeType.toUpperCase() !== 'Z_TOKEN') {
      data.request_z_token = true;
    }
    const payload = { data };

    try {
      const response = await axios.post(endpoint, payload, {
        headers: {
          ...headers,
          Authorization: `Bearer ${challengeToken}`,
        },
        validateStatus: (status) => status === 200 || status === 201 || status === 202,
      });
      return sessionResponseSchema.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function startPasswordReset(email: string): Promise<void> {
    const endpoint =
      config.passwordResetStartEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/password_reset/start');

    try {
      await axios.post(
        endpoint,
        { email },
        {
          headers,
          validateStatus: (status) => status === 204,
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function completePasswordReset(
    token: string,
    password: string,
    passwordConfirmation: string
  ): Promise<void> {
    const endpoint =
      config.passwordResetCompleteEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/password_reset/complete');

    try {
      await axios.post(
        endpoint,
        { token, password, password_confirmation: passwordConfirmation },
        {
          headers,
          validateStatus: (status) => status === 204,
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function startEmailLogin(email: string): Promise<{ message: string }> {
    const endpoint =
      config.emailLoginStartEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/login/email/start');

    try {
      const response = await axios.post(endpoint, { email }, { headers });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function verifyEmailLogin(token: string): Promise<LoginResponse> {
    const endpoint =
      config.emailLoginVerifyEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/login/email/verify');

    try {
      const response = await axios.post(endpoint, { token }, { headers });

      if (response.status === 201) {
        return sessionResponseSchema.parse(response.data);
      }
      if (response.status === 202) {
        return challengeResponseSchema.parse(response.data);
      }
      throw new Error('Unexpected response status');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function generateBackupTokens(authToken: string): Promise<{ tokens: string[] }> {
    const endpoint =
      config.backupTokensGenerateEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/mfa/backup_tokens/generate');

    try {
      const response = await axios.post(
        endpoint,
        {},
        { headers: { ...headers, Authorization: `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  async function getBackupTokenCount(authToken: string): Promise<{ count: number }> {
    const endpoint =
      config.backupTokensCountEndpoint ||
      deriveEndpoint(config.passwordLoginEndpoint, '/mfa/backup_tokens/count');

    try {
      const response = await axios.get(endpoint, {
        headers: { ...headers, Authorization: `Bearer ${authToken}` },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw parseErrorResponse(error.response.data);
      }
      throw error;
    }
  }

  return {
    passwordLogin,
    startChallenge,
    verifyChallenge,
    startPasswordReset,
    completePasswordReset,
    startEmailLogin,
    verifyEmailLogin,
    generateBackupTokens,
    getBackupTokenCount,
  };
}

export type AuthClient = ReturnType<typeof createAuthClient>;
